<!-- MyComponent.vue -->
<script setup lang="ts">
import { ArrowUpDown, RotateCcw } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import TheSortingFilter from '@/components/mode-list-filters/TheSortingFilter.vue';
import TheDurationDropdown from '@/components/mode-list/TheDurationDropdown.vue';
import TheGrid from '@/components/mode-list/TheGrid.vue';
import TheMobileSlider from '@/components/mode-list/TheMobileSlider.vue';
import TheMultiselectDropdown from '@/components/mode-list/TheMultiselectDropdown.vue';
import ThePriceDropdown from '@/components/mode-list/ThePriceDropdown.vue';
import TheSelectDropdown from '@/components/mode-list/TheSelectDropdown.vue';
import TheTimeRangeDropdown from '@/components/mode-list/TheTimeRangeDropdown.vue';
import useResponsive from '@/composables/useResponsive';
import { useFiltersStore } from '@/stores/useFiltersStore';
import { useMetricsStore } from '@/stores/useMetricsStore';
import { useProductsStore } from '@/stores/useProductsStore';

import TheRDBKCalendarDropdown from './TheRDBKCalendarDropdown.vue';
import TheRDBKMobileSlider from './TheRDBKMobileSlider.vue';

const { t } = useI18n();
const filtersStore = useFiltersStore();
const { filters } = storeToRefs(filtersStore);

const productsStore = useProductsStore();
const { totalResults } = storeToRefs(productsStore);

const metricsStore = useMetricsStore();
const { places } = storeToRefs(metricsStore);
const { isMobile } = useResponsive();

const forceRefreshKey = ref<number>(0);

const sortingOptions2 = [
  { label: t('menu.relevance'), value: 'random' },
  { label: t('menu.price_asc'), value: 'price_from_ascending' },
  { label: t('menu.price_desc'), value: 'price_from_descending' },
  { label: t('menu.ranking'), value: 'rating' },
];

const timeRangeOptions = [
  { label: t('timeRange.morning'), minTime: 480, maxTime: 720, count: 0 },
  { label: t('timeRange.afternoon'), minTime: 720, maxTime: 1020, count: 0 },
  { label: t('timeRange.evening'), minTime: 1020, maxTime: 1440, count: 0 },
];

const isSortingOpen = ref<boolean>(false);
const sortingMode = ref(sortingOptions2[0]);

function handleSortingFilter(option: any) {
  sortingMode.value = option;
  isSortingOpen.value = false;
  filtersStore.addSortingFilter(option.value);
}

function handleResetFilters() {
  filtersStore.resetRdbkFilters();
  forceRefreshKey.value++;
}

onMounted(async () => {
  filtersStore.loadFromStorage();
  await productsStore.fetchProductsList(filters.value);
});
</script>

<template>
  <div class="yb-preset">
    <div v-if="!isMobile" class="dropdowns_wrapper">
      <div class="filters">
        <TheRDBKCalendarDropdown :key="forceRefreshKey" :placeholder="t('menu.dates')" alignment="left" />
        <ThePriceDropdown :key="forceRefreshKey" :placeholder="t('menu.price')" />
        <TheDurationDropdown :key="forceRefreshKey" :placeholder="t('menu.duration')" />
        <TheTimeRangeDropdown :key="forceRefreshKey" :placeholder="t('menu.moment')" :options="timeRangeOptions" />
        <TheMultiselectDropdown
          v-if="places.length" :key="forceRefreshKey" :placeholder="t('menu.place')"
          :options="places" @update:model-value="filtersStore.addPlacesFilters"
        />
      </div>
      <div class="sorting">
        <TheSelectDropdown
          :options="sortingOptions2" alignment="right"
          @update:model-value="(opt: any) => filtersStore.addSortingFilter(opt.value)"
        />
      </div>
    </div>
    <TheRDBKMobileSlider v-if="isMobile" />
    <div v-if="!isMobile" class="results_desktop">
      <div>
        <p v-if="totalResults" class="results">
          <span>{{ totalResults }}</span> {{ t('list.total') }}
        </p>
        <p v-if="!totalResults" class="results">
          <span>0</span> {{ t('list.total') }}
        </p>
      </div>
      <div>
        <button class="refresh-button" @click="handleResetFilters">
          {{ t('menu.reset') }}<span><RotateCcw color="white" :size="18" /></span>
        </button>
      </div>
    </div>
    <div v-else class="results_mobile">
      <p v-if="totalResults" class="results">
        <span>{{ totalResults }}</span> {{ t('list.total') }}
      </p>
      <p v-else class="results">
        <span>0</span> {{ t('list.total') }}
      </p>
      <button type="button" class="sorting_button" @click="isSortingOpen = !isSortingOpen">
        {{ t('menu.sorting') }} <span><ArrowUpDown :size="18" color="var(--yb-color-black)" /></span>
      </button>
    </div>
    <TheMobileSlider
      :is-open="isSortingOpen" :title="t('menu.sorting')" anchor="bottom" :footer="false"
      @close="isSortingOpen = false"
    >
      <TheSortingFilter
        :options="sortingOptions2" :selected-option="sortingMode"
        @select="(val: any) => handleSortingFilter(val)"
      />
    </TheMobileSlider>
    <TheGrid />
  </div>
</template>

<style lang="css" scoped>
.results {
  white-space: nowrap;
  text-align: left;
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-light);
  line-height: var(--yb-text-lineheight);

  span {
    font-weight: var(--yb-text-bold);
  }

  @media screen and (width < 640px) {
    font-size: var(--yb-text-xs);
  }
}

.results_mobile {
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: var(--yb-text-lineheight);

  @media screen and (width < 1200px) {
    margin-bottom: var(--yb-spacing-sm);
  }

  button {
    all: unset;
    display: flex;
    align-items: center;
    font-weight: var(--yb-text-bold);
    cursor: pointer;

    span {
      margin-left: var(--yb-spacing-xs);

      svg {
        position: relative;
        top: 4px;
      }
    }
  }
}

.sorting_button {
  white-space: nowrap;
  @media screen and (width < 640px) {
    font-size: var(--yb-text-sm);
  }
}

.results_desktop {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dropdowns_wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  /*margin-top: var(--yb-spacing-base);*/
  margin-bottom: var(--yb-spacing-base);
}

.filters,
.sorting {
  display: flex;
  align-items: center;
  gap: var(--yb-spacing-sm);
}

@media screen and (width < 1024px) {
  .filters,
  .sorting {
    display: none;
  }
}

.refresh-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--yb-spacing-xs);
  color: var(--yb-color-white);
  background-color: var(--yb-color-primary);
  padding: 0.5rem 0.65rem;
  margin-left: auto;
  margin-bottom: var(--yb-spacing-sm);
  border: none;
  border-radius: var(--yb-radius-sm);
  cursor: pointer;

  &:hover {
    background-color: var(--yb-color-dark-primary);
  }

  svg {
    position: relative;
    top: 2px;
  }
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 1s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

.slide-enter-to,
.slide-leave-from {
  transform: translateX(0);
}
</style>
